import { ROUTES } from '@/utils/constants';
import { Link } from 'react-router-dom';

function spiral(
  cx: number,
  cy: number,
  turns: number,
  gapPx: number,
  startR: number,
  rotation = 0,
): string {
  const step = Math.PI / 60;
  const max = turns * 2 * Math.PI;
  const pts: string[] = [];
  for (let t = 0; t <= max; t += step) {
    const r = startR + (gapPx / (2 * Math.PI)) * t;
    const x = cx + r * Math.cos(t + rotation);
    const y = cy + r * Math.sin(t + rotation);
    pts.push(
      t === 0
        ? `M ${x.toFixed(1)},${y.toFixed(1)}`
        : `L ${x.toFixed(1)},${y.toFixed(1)}`,
    );
  }
  return pts.join(' ');
}

function CtaSpiral() {
  const cx = 720,
    cy = 300;
  return (
    <svg
      className="landing-cta__art"
      viewBox="0 0 1440 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <path
        d={spiral(cx, cy, 5, 160, 12)}
        fill="none"
        stroke="white"
        strokeWidth="1.2"
        strokeOpacity="0.038"
      />
      <path
        d={spiral(cx, cy, 4, 180, 20, Math.PI)}
        fill="none"
        stroke="white"
        strokeWidth="1"
        strokeOpacity="0.025"
      />
      <path
        d={spiral(cx, cy, 6, 130, 8, Math.PI * 0.5)}
        fill="none"
        stroke="white"
        strokeWidth="0.8"
        strokeOpacity="0.018"
      />
    </svg>
  );
}

function HiwArt() {
  return (
    <svg
      className="landing-hiw__art"
      viewBox="0 0 1440 620"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <clipPath id="hiw-c1">
          <rect x="30" y="40" width="190" height="110" />
        </clipPath>
        <clipPath id="hiw-c2">
          <rect x="1220" y="30" width="190" height="110" />
        </clipPath>
        <clipPath id="hiw-c3">
          <rect x="18" y="255" width="150" height="130" />
        </clipPath>
        <clipPath id="hiw-c4">
          <rect x="1272" y="220" width="150" height="130" />
        </clipPath>
        <clipPath id="hiw-c5">
          <rect x="55" y="455" width="190" height="110" />
        </clipPath>
        <clipPath id="hiw-c6">
          <rect x="1195" y="445" width="190" height="110" />
        </clipPath>
        <clipPath id="hiw-c7">
          <rect x="570" y="18" width="160" height="80" />
        </clipPath>
        <clipPath id="hiw-c8">
          <rect x="708" y="508" width="160" height="80" />
        </clipPath>
      </defs>

      {/* waves — concentric arcs from bottom-left corner, top-left tile */}
      <g clipPath="url(#hiw-c1)" opacity="0.10">
        <circle
          cx="30"
          cy="150"
          r="60"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
        <circle
          cx="30"
          cy="150"
          r="100"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
        <circle
          cx="30"
          cy="150"
          r="142"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
        <circle
          cx="30"
          cy="150"
          r="185"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
        <circle
          cx="30"
          cy="150"
          r="228"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
      </g>

      {/* diagonal lines — top-right tile */}
      <g clipPath="url(#hiw-c2)" opacity="0.09">
        <path
          fill="none"
          stroke="black"
          strokeWidth="1"
          d="M1110,140 L1220,30 M1132,140 L1242,30 M1154,140 L1264,30 M1176,140 L1286,30 M1198,140 L1308,30 M1220,140 L1330,30 M1242,140 L1352,30 M1264,140 L1374,30 M1286,140 L1396,30 M1308,140 L1418,30 M1330,140 L1440,30 M1352,140 L1462,30 M1374,140 L1484,30"
        />
      </g>

      {/* dot grid — mid-left tile */}
      <g clipPath="url(#hiw-c3)" opacity="0.12">
        {[30, 62, 94, 126, 158].map((x) => (
          <circle key={`d1-${x}`} cx={x} cy={273} r="3.5" fill="black" />
        ))}
        {[46, 78, 110, 142].map((x) => (
          <circle key={`d2-${x}`} cx={x} cy={301} r="3.5" fill="black" />
        ))}
        {[30, 62, 94, 126, 158].map((x) => (
          <circle key={`d3-${x}`} cx={x} cy={329} r="3.5" fill="black" />
        ))}
        {[46, 78, 110, 142].map((x) => (
          <circle key={`d4-${x}`} cx={x} cy={357} r="3.5" fill="black" />
        ))}
      </g>

      {/* concentric diamonds — mid-right tile */}
      <g clipPath="url(#hiw-c4)" opacity="0.09">
        <polygon
          points="1422,285 1352,350 1422,415 1492,350"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
        <polygon
          points="1422,245 1312,350 1422,455 1532,350"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
        <polygon
          points="1422,205 1272,350 1422,495 1572,350"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
        <polygon
          points="1422,165 1232,350 1422,535 1612,350"
          fill="none"
          stroke="black"
          strokeWidth="1.5"
        />
      </g>

      {/* diagonal bars — bottom-left tile */}
      <g clipPath="url(#hiw-c5)" opacity="0.08">
        <g transform="rotate(-13, 150, 510)">
          <rect x="15" y="460" width="280" height="18" fill="black" />
          <rect x="15" y="484" width="280" height="12" fill="black" />
          <rect x="15" y="502" width="280" height="16" fill="black" />
          <rect x="15" y="524" width="280" height="10" fill="black" />
        </g>
      </g>

      {/* overlapping filled circles — bottom-right tile */}
      <g clipPath="url(#hiw-c6)" opacity="0.07">
        <circle cx="1352" cy="442" r="75" fill="black" />
        <circle cx="1210" cy="548" r="58" fill="black" />
        <circle cx="1308" cy="498" r="26" fill="black" />
        <circle cx="1422" cy="540" r="44" fill="black" />
      </g>

      {/* small dot cluster — center-top */}
      <g clipPath="url(#hiw-c7)" opacity="0.11">
        {[584, 616, 648, 680, 712].map((x) => (
          <circle key={`ct1-${x}`} cx={x} cy={36} r="3.5" fill="black" />
        ))}
        {[600, 632, 664, 696].map((x) => (
          <circle key={`ct2-${x}`} cx={x} cy={64} r="3.5" fill="black" />
        ))}
        {[584, 616, 648, 680, 712].map((x) => (
          <circle key={`ct3-${x}`} cx={x} cy={88} r="3.5" fill="black" />
        ))}
      </g>

      {/* small diagonal lines — center-bottom */}
      <g clipPath="url(#hiw-c8)" opacity="0.09">
        <path
          fill="none"
          stroke="black"
          strokeWidth="1"
          d="M648,588 L708,508 M670,588 L730,508 M692,588 L752,508 M714,588 L774,508 M736,588 L796,508 M758,588 L818,508 M780,588 L840,508 M802,588 L862,508 M824,588 L884,508"
        />
      </g>
    </svg>
  );
}

const STEPS = [
  {
    num: '01',
    title: 'Create your page',
    desc: 'Sign up and claim your Nestra URL in under a minute.',
  },
  {
    num: '02',
    title: 'Add your content',
    desc: 'Releases, events, merch, and platform links, all from your dashboard.',
  },
  {
    num: '03',
    title: 'Share everywhere',
    desc: 'One link in your bio reaches your whole audience, every time.',
  },
];

export function LandingHowItWorks() {
  return (
    <>
      <section className="landing-hiw">
        <HiwArt />
        <div className="landing-wide landing-hiw__content">
          <h2 className="landing-hiw__title">Up in minutes</h2>
          <div className="landing-hiw__steps">
            {STEPS.map((step) => (
              <div key={step.num} className="landing-hiw__step">
                <span className="landing-hiw__num">{step.num}</span>
                <h3 className="landing-hiw__step-title">{step.title}</h3>
                <p className="landing-hiw__step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-cta">
        <CtaSpiral />
        <div className="landing-wide landing-cta__content">
          <h2 className="landing-cta__title">Ready to own your presence?</h2>
          <p className="landing-cta__sub">
            Free to start. No credit card required.
          </p>
          <Link to={ROUTES.SIGNUP} className="landing-btn landing-btn--coral">
            Claim your page
          </Link>
        </div>
      </section>
    </>
  );
}
